/**
 * Mobile ESS — My Attendance & Payslips
 * Employee self-service: attendance history, payslips, leave balance.
 * APIs: /hr/me  /hr/me/payslips  /hr/me/attendance?month=  /hr/me/leave-balance
 */
import React, { useEffect, useState } from 'react';
import { apiGet } from '../../lib/api';
import { formatISTTime } from '../../lib/format';
import { toast } from 'sonner';
import {
  CalendarDays, FileText, Wallet, Clock, CheckCircle2,
  XCircle, MinusCircle, ChevronLeft, ChevronRight, Download,
} from 'lucide-react';

const MONTHS = ['Jan','Feb','Mar','Apr','May','Jun',
                'Jul','Aug','Sep','Oct','Nov','Dec'];

function monthLabel(m) {
  const [y, mo] = m.split('-');
  return `${MONTHS[parseInt(mo, 10) - 1]} ${y}`;
}

function nowMonth() {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`;
}

function prevMonth(m) {
  const [y, mo] = m.split('-').map(Number);
  const d = new Date(y, mo - 2, 1);
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`;
}

function nextMonth(m) {
  const [y, mo] = m.split('-').map(Number);
  const d = new Date(y, mo, 1);
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`;
}

const STATUS_STYLE = {
  Present: { icon: CheckCircle2, cls: 'text-emerald-600', label: 'P' },
  Absent:  { icon: XCircle,      cls: 'text-rose-500',    label: 'A' },
  Leave:   { icon: MinusCircle,  cls: 'text-amber-500',   label: 'L' },
  Holiday: { icon: MinusCircle,  cls: 'text-teal-400',    label: 'H' },
  default: { icon: MinusCircle,  cls: 'text-slate-300',   label: '—' },
};

export default function MobileESS() {
  const [tab, setTab]             = useState('attendance');
  const [month, setMonth]         = useState(nowMonth());
  const [profile, setProfile]     = useState(null);
  const [attendance, setAttendance] = useState([]);
  const [payslips, setPayslips]   = useState([]);
  const [leaveBalance, setLeaveBalance] = useState([]);
  const [loading, setLoading]     = useState(true);

  useEffect(() => {
    Promise.all([
      apiGet('/hr/me').catch(() => null),
      apiGet('/hr/me/leave-balance').catch(() => ({ balance: [] })),
      apiGet('/hr/me/payslips').catch(() => ({ payslips: [] })),
    ]).then(([me, lb, ps]) => {
      setProfile(me?.employee || null);
      setLeaveBalance(lb?.balance || []);
      setPayslips(ps?.payslips || []);
      setLoading(false);
    }).catch(() => setLoading(false));
  }, []);

  useEffect(() => {
    if (!profile) return;
    apiGet(`/hr/me/attendance?month=${month}`)
      .then(r => setAttendance(r?.attendance || []))
      .catch(() => setAttendance([]));
  }, [month, profile]);

  if (loading) return (
    <div className="flex items-center justify-center min-h-[60vh] text-slate-400 text-sm" data-testid="m-ess-loading">
      Loading your records…
    </div>
  );

  if (!profile) return (
    <div className="flex flex-col items-center justify-center min-h-[60vh] px-6 text-center" data-testid="m-ess-no-profile">
      <FileText size={36} className="text-slate-300 mb-3" />
      <p className="text-sm text-slate-500 font-display font-bold">Not linked to an employee record</p>
      <p className="text-xs text-slate-400 mt-1">Ask HR to link your login to your employee profile.</p>
    </div>
  );

  const present = attendance.filter(a => a.status === 'Present').length;
  const absent  = attendance.filter(a => a.status === 'Absent').length;
  const leaves  = attendance.filter(a => a.status === 'Leave').length;

  return (
    <div className="min-h-screen bg-slate-50 pb-24" data-testid="m-ess-page">
      {/* Header */}
      <div className="bg-[#1F7A6E] text-white px-4 pt-4 pb-6">
        <p className="text-xs font-display font-bold uppercase tracking-widest text-white/60 mb-1">
          My Profile
        </p>
        <h1 className="font-display font-bold text-xl">{profile.name}</h1>
        <p className="text-sm text-white/70 mt-0.5">
          {profile.designation || profile.role} · {profile.department}
        </p>
        <p className="text-xs text-white/50 mt-0.5">{profile.employee_id || ''}</p>
      </div>

      {/* Tab bar */}
      <div className="flex bg-white border-b border-slate-200 sticky top-0 z-10">
        {[
          { key: 'attendance', label: 'Attendance', icon: CalendarDays },
          { key: 'payslips',   label: 'Payslips',   icon: FileText    },
          { key: 'leave',      label: 'Leave',       icon: Wallet      },
        ].map(({ key, label, icon: Icon }) => (
          <button
            key={key}
            onClick={() => setTab(key)}
            data-testid={`m-ess-tab-${key}`}
            className={`flex-1 flex flex-col items-center gap-0.5 py-2.5 text-[11px] font-display font-bold border-b-2 transition-colors ${
              tab === key ? 'border-[#1F7A6E] text-[#1F7A6E]' : 'border-transparent text-slate-400'
            }`}
          >
            <Icon size={16} />
            {label}
          </button>
        ))}
      </div>

      {/* ── Attendance tab ── */}
      {tab === 'attendance' && (
        <div className="px-4 pt-4">
          {/* Month nav */}
          <div className="flex items-center justify-between mb-4">
            <button onClick={() => setMonth(prevMonth(month))} className="p-2 rounded-full hover:bg-slate-200" data-testid="m-ess-prev-month">
              <ChevronLeft size={18} className="text-slate-600" />
            </button>
            <span className="font-display font-bold text-slate-800">{monthLabel(month)}</span>
            <button
              onClick={() => setMonth(nextMonth(month))}
              disabled={month >= nowMonth()}
              className="p-2 rounded-full hover:bg-slate-200 disabled:opacity-30"
              data-testid="m-ess-next-month"
            >
              <ChevronRight size={18} className="text-slate-600" />
            </button>
          </div>

          {/* Summary chips */}
          <div className="grid grid-cols-3 gap-2 mb-4">
            {[
              { label: 'Present', value: present, cls: 'bg-emerald-50 text-emerald-700' },
              { label: 'Absent',  value: absent,  cls: 'bg-rose-50 text-rose-700'     },
              { label: 'Leave',   value: leaves,  cls: 'bg-amber-50 text-amber-700'   },
            ].map(({ label, value, cls }) => (
              <div key={label} className={`${cls} rounded-xl py-3 text-center`}>
                <div className="font-display font-extrabold text-2xl">{value}</div>
                <div className="text-[10px] font-bold uppercase tracking-wider mt-0.5">{label}</div>
              </div>
            ))}
          </div>

          {/* Day list */}
          {attendance.length === 0
            ? <p className="text-center text-sm text-slate-400 py-8">No records for this month.</p>
            : (
              <div className="space-y-1.5">
                {[...attendance].reverse().map(a => {
                  const s = STATUS_STYLE[a.status] || STATUS_STYLE.default;
                  const Icon = s.icon;
                  return (
                    <div key={a.date}
                      className="bg-white rounded-xl px-4 py-3 flex items-center justify-between border border-slate-100">
                      <div>
                        <p className="font-display font-bold text-[13px] text-slate-800">
                          {new Date(a.date).toLocaleDateString('en-IN', { weekday: 'short', day: 'numeric', month: 'short' })}
                        </p>
                        {(a.check_in || a.check_out) && (
                          <p className="text-[11px] text-slate-400 mt-0.5 flex items-center gap-1">
                            <Clock size={10} />
                            {a.check_in ? formatISTTime(a.check_in) : '--:--'}
                            {' → '}
                            {a.check_out ? formatISTTime(a.check_out) : '--:--'}
                            {a.hours_worked ? ` · ${parseFloat(a.hours_worked).toFixed(1)}h` : ''}
                          </p>
                        )}
                      </div>
                      <div className={`flex items-center gap-1 ${s.cls}`}>
                        <Icon size={16} />
                        <span className="font-display font-bold text-sm">{a.status || '—'}</span>
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
        </div>
      )}

      {/* ── Payslips tab ── */}
      {tab === 'payslips' && (
        <div className="px-4 pt-4">
          {payslips.length === 0
            ? <p className="text-center text-sm text-slate-400 py-8">No payslips available yet.</p>
            : (
              <div className="space-y-3">
                {payslips.map(p => (
                  <div key={p.id} className="bg-white rounded-xl px-4 py-4 border border-slate-100" data-testid={`m-ess-payslip-${p.id}`}>
                    <div className="flex items-start justify-between">
                      <div>
                        <p className="font-display font-bold text-slate-800">{p.month_label || p.month}</p>
                        <p className="text-xs text-slate-400 mt-0.5">{p.days_worked} working days</p>
                      </div>
                      <p className="font-display font-extrabold text-lg text-[#1F7A6E]">
                        ₹{Number(p.net_pay || 0).toLocaleString('en-IN')}
                      </p>
                    </div>
                    <div className="mt-2 pt-2 border-t border-slate-100 flex items-center justify-between">
                      <div className="text-[11px] text-slate-500 space-y-0.5">
                        <p>Gross: ₹{Number(p.gross_pay || 0).toLocaleString('en-IN')}</p>
                        <p>Deductions: ₹{Number(p.total_deductions || 0).toLocaleString('en-IN')}</p>
                      </div>
                      {p.pdf_url && (
                        <a href={p.pdf_url} target="_blank" rel="noreferrer"
                          className="flex items-center gap-1.5 px-3 py-1.5 bg-[#1F7A6E] text-white rounded-lg text-[11px] font-display font-bold"
                          data-testid={`m-ess-payslip-dl-${p.id}`}>
                          <Download size={12} /> Download
                        </a>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            )}
        </div>
      )}

      {/* ── Leave balance tab ── */}
      {tab === 'leave' && (
        <div className="px-4 pt-4">
          {leaveBalance.length === 0
            ? <p className="text-center text-sm text-slate-400 py-8">No leave balance data.</p>
            : (
              <div className="space-y-3">
                {leaveBalance.map(lb => (
                  <div key={lb.leave_type} className="bg-white rounded-xl px-4 py-4 border border-slate-100" data-testid={`m-ess-leave-${lb.leave_type}`}>
                    <div className="flex items-center justify-between mb-2">
                      <p className="font-display font-bold text-slate-800">{lb.leave_type}</p>
                      <p className="font-display font-extrabold text-xl text-[#1F7A6E]">
                        {lb.balance ?? lb.remaining ?? 0}
                        <span className="text-xs font-normal text-slate-400"> days left</span>
                      </p>
                    </div>
                    <div className="h-1.5 bg-slate-100 rounded-full overflow-hidden">
                      <div
                        className="h-full bg-[#1F7A6E] rounded-full"
                        style={{ width: lb.entitled ? `${Math.min(100, ((lb.balance ?? 0) / lb.entitled) * 100)}%` : '0%' }}
                      />
                    </div>
                    <p className="text-[10px] text-slate-400 mt-1">
                      {lb.taken ?? 0} taken · {lb.entitled ?? 0} entitled
                    </p>
                  </div>
                ))}
              </div>
            )}
        </div>
      )}
    </div>
  );
}
